import React from 'react';

interface Point {
  x: number;
  y: number;
  lat: number;
  lng: number;
}

interface CompletedArea {
  id: string;
  points: Point[];
  type: 'polygon';
  customerIds: string[];
}

interface MultiAreaOverlayProps {
  completedAreas: CompletedArea[];
  currentPoints: Point[];
  currentMousePoint: Point | null;
  isDrawing: boolean;
}

export const MultiAreaOverlay: React.FC<MultiAreaOverlayProps> = ({
  completedAreas,
  currentPoints,
  currentMousePoint,
  isDrawing
}) => {
  // Render completed polygon areas
  const renderCompletedAreas = () => {
    return completedAreas.map((area, index) => {
      const points = area.points.map(p => `${p.x},${p.y}`).join(' ');
      return (
        <g key={area.id}>
          {/* Filled area */}
          <polygon
            points={points}
            fill="rgba(34, 197, 94, 0.2)"
            stroke="rgba(34, 197, 94, 0.8)"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
          {/* Area label */}
          <text
            x={area.points[0].x}
            y={area.points[0].y - 15}
            fill="#16a34a"
            fontSize="12"
            fontWeight="bold"
            style={{ textShadow: '0 0 3px white' }}
          >
            Alan {index + 1} ({area.customerIds.length} müşteri)
          </text>
        </g>
      );
    });
  };

  // Render current polygon drawing
  const renderCurrentDrawing = () => {
    if (!isDrawing || currentPoints.length === 0) return null;

    // Polygon drawing
    let path = `M ${currentPoints[0].x} ${currentPoints[0].y}`;
    for (let i = 1; i < currentPoints.length; i++) {
      path += ` L ${currentPoints[i].x} ${currentPoints[i].y}`;
    }
    if (currentMousePoint) {
      path += ` L ${currentMousePoint.x} ${currentMousePoint.y}`;
    }

    const polygonPoints = currentPoints.map(p => `${p.x},${p.y}`).join(' ') +
      (currentMousePoint ? ` ${currentMousePoint.x},${currentMousePoint.y}` : '');

    return (
      <g>
        {/* Filled area */}
        {currentPoints.length >= 2 && (
          <polygon
            points={polygonPoints}
            fill="rgba(59, 130, 246, 0.15)"
            stroke="none"
          />
        )}
        {/* Path */}
        <path
          d={path}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeDasharray="5,5"
        />
        {/* Points */}
        {currentPoints.map((point, index) => (
          <g key={index}>
            <circle cx={point.x} cy={point.y} r="6" fill="#3b82f6" stroke="white" strokeWidth="2" />
            <text x={point.x} y={point.y - 12} textAnchor="middle" fill="#1e40af" fontSize="12" fontWeight="bold">
              {index + 1}
            </text>
          </g>
        ))}
        {/* Current mouse */}
        {currentMousePoint && currentPoints.length > 0 && (
          <>
            <line
              x1={currentPoints[currentPoints.length - 1].x}
              y1={currentPoints[currentPoints.length - 1].y}
              x2={currentMousePoint.x}
              y2={currentMousePoint.y}
              stroke="#3b82f6"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
            <circle cx={currentMousePoint.x} cy={currentMousePoint.y} r="4" fill="#93c5fd" stroke="white" strokeWidth="2" />
          </>
        )}
      </g>
    );
  };

  return (
    <svg
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10000
      }}
    >
      {renderCompletedAreas()}
      {renderCurrentDrawing()}
    </svg>
  );
};
